import classNames from 'classnames';
import { useId } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { useUiMessages } from '../../messages';
import { Typography } from '../Typography';

import styles from './ProgressBar.module.css';

export type ProgressValuePosition = 'none' | 'right' | 'floating';

/**
 * Fill colour. `brand` is the default; the semantic scales exist for progress that reports a
 * *score band* rather than completion (a Sash IQ factor, say), where the colour is the reading.
 */
/**
 * Colours a progress reading may take. `warning` and `yellow` are both here on purpose: a design
 * binds one or the other per screen, and the Portal's session screens bind Warning where the Sash
 * IQ page binds Yellow for the same band.
 */
export type ProgressColor = 'brand' | 'success' | 'blue' | 'yellow' | 'warning' | 'error';

export interface ProgressBarProps {
  /** Current value (clamped to `0…max`). */
  value: number;
  /** Maximum value. */
  max?: number;
  /** Fill colour (defaults to the brand fill). */
  color?: ProgressColor;
  /** Where to show the value label. */
  valuePosition?: ProgressValuePosition;
  /** Render a dot marker at the fill's leading edge. */
  marker?: boolean;
  /** Format the value label (defaults to a rounded percentage). */
  formatValue?: (percent: number, value: number, max: number) => ReactNode;
  /** Accessible name (falls back to a generic one). */
  ariaLabel?: string;
  /**
   * Spoken in place of the raw number. A stepper's bar means "step 3 of 7", and hearing "3" is
   * not that; anything whose value is a position rather than a quantity should say so.
   */
  ariaValueText?: string;
  /** Associate an existing visible label instead of `ariaLabel`. */
  ariaLabelledby?: string;
  id?: string;
  className?: string;
}

const FULL_PERCENT = 100;
const clamp = (n: number, min: number, max: number) => Math.min(Math.max(n, min), max);

/**
 * Linear progress indicator following the WAI-ARIA `progressbar` pattern (`aria-valuenow`
 * / `-valuemin` / `-valuemax`). The value can render inline on the right or as a floating
 * bubble tracking the fill's edge, with an optional marker dot.
 *
 * @interim Seeded from the Figma "Progress bar"; replaceable by the UI Kit without API breakage.
 */
export function ProgressBar({
  value,
  max = FULL_PERCENT,
  color = 'brand',
  valuePosition = 'none',
  marker = false,
  formatValue = (percent) => `${String(Math.round(percent))}%`,
  ariaLabel,
  ariaValueText,
  ariaLabelledby,
  id,
  className,
}: ProgressBarProps) {
  const messages = useUiMessages();
  const resolvedAriaLabel = ariaLabel ?? messages.progress?.root ?? 'Progress';
  const autoId = useId();
  const rootId = id ?? autoId;
  const safeMax = max <= 0 ? FULL_PERCENT : max;
  const clamped = clamp(value, 0, safeMax);
  const percent = (clamped / safeMax) * FULL_PERCENT;
  const label = formatValue(percent, clamped, safeMax);

  const track = (
    <div
      role="progressbar"
      id={rootId}
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={safeMax}
      {...(ariaValueText == null ? {} : { 'aria-valuetext': ariaValueText })}
      aria-label={ariaLabelledby == null ? resolvedAriaLabel : undefined}
      aria-labelledby={ariaLabelledby}
      className={classNames(styles.track, styles[`color-${color}`])}
    >
      <div className={styles.fill} style={{ width: `${String(percent)}%` }}>
        {marker && <span className={styles.marker} aria-hidden="true" />}
      </div>
    </div>
  );

  if (valuePosition === 'floating') {
    const style = { '--progress-percent': `${String(percent)}%` } as CSSProperties;
    return (
      <div className={classNames(styles.root, styles.hasFloating, className)} style={style}>
        <div className={styles.bubble} aria-hidden="true">
          <Typography as="span" variant="text-xs" weight="semibold">
            {label}
          </Typography>
        </div>
        {track}
      </div>
    );
  }

  if (valuePosition === 'right') {
    return (
      <div className={classNames(styles.root, styles.hasRight, className)}>
        {track}
        <Typography
          as="span"
          variant="text-sm"
          weight="medium"
          className={classNames(styles.value)}
        >
          {label}
        </Typography>
      </div>
    );
  }

  return <div className={classNames(styles.root, className)}>{track}</div>;
}
